/* eslint-disable react/no-unescaped-entities */
import {
  Box,
  Chip,
  Divider,
  Icon,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from "@outschool/backpack";
import {
  OSSubscriptions,
  SUBSCRIPTION_PLANS,
  applyServiceFee,
  centsToDollars
} from "@outschool/business-rules";
import { PaymentScheduleTypes } from "@outschool/gql-backend-generated";
import {
  ChooseAPlanEnrollmentsQueryQuery,
  ChooseAPlanEnrollmentsQueryQueryVariables
} from "@outschool/gql-frontend-generated";
import {
  faArrowRight,
  faInfoCircle,
  faPartyHorn,
  faQuestionCircle
} from "@outschool/icons";
import { renderCurrencyString, useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import * as Time from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { ExternalLink, TrackedButton } from "@outschool/ui-components-shared";
import { CoinIcon } from "@outschool/ui-components-website";
import { Input, Modal } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { Trans } from "react-i18next";
import { useLocation } from "react-router-dom";

import * as Env from "../../../../shared/Env";

export type SubscriptionPlanInfo = {
  title: string;
  numCredits: number;
  centsPerMonth: number;
  savingsPercentage: number;
  classPerWeekAvg: number;
  numRollOverCredits: number;
  numFreeCredits: number;
};

export const chooseAPlanEnrollmentsQuery = gql`
  query ChooseAPlanEnrollmentsQuery($start: DateTime!, $end: DateTime!) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        uid
        price_cents
        ongoingStartTime
        service_fee_percentage
        section {
          uid
          activity {
            uid
            title
            hasTeacherSchedule
          }
          meetings {
            uid
            start_time
            end_time
            paidAt
          }
        }
        paymentSchedule {
          uid
          scheduleType
          amountCents
        }
      }
    }
  }
`;

const ChooseAPlan = React.forwardRef((_props, ref) => {
  const { t } = useTranslation("client\\routes\\JoinMembershipPage");
  const { currentUser, isLoggedIn, currentUserIsLoading } = useSession();
  const theme = useTheme();
  const isNewBuyer =
    // no need to check CU if logged out
    !isLoggedIn ||
    // assume false if CU is loading so we don't show it then hide
    (!currentUserIsLoading && !currentUser?.firstPaidEnrollment);

  const containerRef = React.useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isLearnMoreModalOpen, setIsLearnMoreModalOpen] = React.useState(false);
  const trackEvent = useTrackEvent();
  const [approxMonthlySpendingAmount, setApproxMonthlySpendingAmount] =
    React.useState(0);
  const [
    approxMonthlySpendingAmountInput,
    setApproxMonthlySpendingAmountInput
  ] = React.useState(0);
  const [showSavingsForm, setShowSavingsForm] = React.useState(false);

  const { start, end } = React.useMemo(() => {
    const now = dayjs();
    return {
      start: now.subtract(1, "month").toDate(),
      end: now.toDate()
    };
  }, []);

  const { data: activeEnrollmentsInThePastMonthData } =
    useQueryWithPreviousData<
      ChooseAPlanEnrollmentsQueryQuery,
      ChooseAPlanEnrollmentsQueryQueryVariables
    >(chooseAPlanEnrollmentsQuery, {
      variables: { start, end }
    });

  const location = useLocation();
  const navigate = useNavigation();
  const queryParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const activeEnrollmentsInThePastMonth: NonNullable<
    NonNullable<
      ChooseAPlanEnrollmentsQueryQuery["currentUser"]
    >["activeEnrollments"]
  > = React.useMemo(() => {
    return (
      activeEnrollmentsInThePastMonthData?.currentUser?.activeEnrollments ?? []
    );
  }, [activeEnrollmentsInThePastMonthData?.currentUser?.activeEnrollments]);

  const suggestedPlan = !showSavingsForm
    ? SUBSCRIPTION_PLANS[1]
    : approxMonthlySpendingAmount * 100 >
      SUBSCRIPTION_PLANS.at(-1)!.centsPerMonth
    ? SUBSCRIPTION_PLANS.at(-1)!
    : SUBSCRIPTION_PLANS.find(
        plan => plan.centsPerMonth >= approxMonthlySpendingAmount * 100
      ) || SUBSCRIPTION_PLANS[0];
  const [selectedPlan, setSelectedPlan] =
    React.useState<(typeof SUBSCRIPTION_PLANS)[0]>(suggestedPlan);

  const numCreditsSuggested = suggestedPlan?.numCredits;
  const savingsAmountSuggested = suggestedPlan?.savingsCents;

  React.useEffect(() => {
    if (activeEnrollmentsInThePastMonth.length > 0) {
      const monthlySpend = Math.round(
        centsToDollars(
          activeEnrollmentsInThePastMonth.reduce((total, enrollment) => {
            if (enrollment && enrollment.paymentSchedule) {
              // Club enrollments
              if (
                enrollment.paymentSchedule &&
                enrollment.paymentSchedule.amountCents &&
                enrollment.paymentSchedule.scheduleType ===
                  PaymentScheduleTypes.MonthlyFirst
              ) {
                total += applyServiceFee(
                  enrollment.paymentSchedule.amountCents,
                  enrollment.service_fee_percentage
                );
                // Weekly enrollments (1:1)
              } else if (
                enrollment &&
                enrollment.price_cents &&
                enrollment?.section?.activity.hasTeacherSchedule
              ) {
                const numMeetings = enrollment.section.meetings.filter(
                  meeting =>
                    dayjs(meeting.start_time).isBefore(end) &&
                    dayjs(meeting.start_time).isAfter(start) &&
                    !!meeting.paidAt
                ).length;
                total += applyServiceFee(
                  enrollment.price_cents * numMeetings,
                  enrollment.service_fee_percentage
                );
                // Weekly enrollments (courses)
              } else if (
                enrollment.paymentSchedule &&
                enrollment.paymentSchedule.amountCents &&
                enrollment.paymentSchedule.scheduleType ===
                  PaymentScheduleTypes.WeeklySunday
              ) {
                total += applyServiceFee(
                  enrollment.paymentSchedule.amountCents * 4,
                  enrollment.service_fee_percentage
                );
              }
              // Weekly enrollments (Ongoing)
            } else if (
              enrollment &&
              enrollment.price_cents &&
              enrollment.ongoingStartTime
            ) {
              total += applyServiceFee(
                enrollment.price_cents * 4,
                enrollment.service_fee_percentage
              );
              // upfront enrollments
            } else if (enrollment && enrollment.price_cents) {
              total += applyServiceFee(
                enrollment.price_cents,
                enrollment.service_fee_percentage
              );
            }
            return total;
          }, 0)
        )
      );
      setShowSavingsForm(monthlySpend >= 50);
      setApproxMonthlySpendingAmount(monthlySpend);
      setApproxMonthlySpendingAmountInput(monthlySpend);
    }
  }, [
    activeEnrollmentsInThePastMonth,
    setApproxMonthlySpendingAmount,
    setApproxMonthlySpendingAmountInput,
    setShowSavingsForm,
    start,
    end
  ]);

  React.useEffect(() => {
    setSelectedPlan(suggestedPlan);
  }, [suggestedPlan]);

  React.useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        // @ts-ignore
        const containerWidth = containerRef.current.offsetWidth;
        // @ts-ignore
        const contentWidth = containerRef.current.scrollWidth;
        setIsOverflowing(contentWidth > containerWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);

  React.useEffect(() => {
    const index = SUBSCRIPTION_PLANS.indexOf(selectedPlan);
    if (containerRef.current) {
      const planElement = containerRef.current.children[index] as HTMLElement;

      containerRef.current.scrollTo({
        left:
          planElement.offsetLeft -
          containerRef.current.offsetWidth / 2 +
          planElement.offsetWidth / 2,
        behavior: "smooth"
      });
    }
  }, [selectedPlan]);

  const suggestedPlanCreditsString = suggestedPlan.numCredits.toString();
  const savingsString = renderCurrencyString({
    priceInCents: suggestedPlan.savingsCents,
    showCurrencyCode: false
  });
  const memberCost = renderCurrencyString({
    priceInCents:
      ((approxMonthlySpendingAmount || suggestedPlan.centsPerMonth / 100) -
        ((approxMonthlySpendingAmount || suggestedPlan.centsPerMonth / 100) *
          suggestedPlan.savingsPercentage) /
          100) *
      100,
    showCurrencyCode: false
  });
  const nonMemberCost = renderCurrencyString({
    priceInCents:
      (approxMonthlySpendingAmount || suggestedPlan?.centsPerMonth / 100) * 100,
    showCurrencyCode: false
  });

  const { enterLoginFlow } = useLoginFlowContext();

  const onSelectPlan = React.useCallback(
    (plan: { title: string }) => {
      if (Env.IS_READ_ONLY_MODE) {
        return;
      }
      const utmSource = queryParams.get("utm_source");
      const utmMedium = queryParams.get("utm_medium");
      const utmCampaign = queryParams.get("utm_campaign");

      trackEvent("JOIN_MEMBERSHIPS_PLAN_SELECTED", {
        plan: plan.title,
        userId: currentUser?.uid,
        utmCampaign,
        utmSource,
        utmMedium
      });

      // eslint-disable-next-line i18next/no-literal-string
      queryParams.set("joinMembershipModal", "true");
      // eslint-disable-next-line i18next/no-literal-string
      queryParams.set("plan", plan.title);
      const newUrl = `${location.pathname}?${queryParams.toString()}`;

      if (isLoggedIn) {
        navigate(newUrl, {
          replace: true
        });
        setIsLearnMoreModalOpen(false);
      } else {
        enterLoginFlow({
          authTrigger: AuthTrigger.BUY_SUBSCRIPTION,
          shouldNavigateOverwrite: false,
          skipLearners: true,
          newUrl
        });
      }
    },
    [
      queryParams,
      navigate,
      isLoggedIn,
      trackEvent,
      enterLoginFlow,
      currentUser?.uid,
      location.pathname
    ]
  );

  return (
    <Box
      ref={ref}
      sx={{
        background: `linear-gradient(to bottom, white, white 33%, ${theme.palette.primary[50]} 33%, ${theme.palette.primary[50]})`
      }}
    >
      <Modal
        open={isLearnMoreModalOpen}
        onClose={() => setIsLearnMoreModalOpen(false)}
      >
        {suggestedPlan && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Trans t={t}>
              <Typography variant="h6">
                With the <b>{suggestedPlanCreditsString} credits plan,</b> you
                can save up to
              </Typography>
              <Box sx={{ marginY: 16 }}>
                <Typography as="span" variant="display1">
                  {savingsString}
                </Typography>
                <Typography as="span" variant="body1">
                  USD
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ marginBottom: 24 }}>
                Member cost: <b> {memberCost}</b> vs Non-member cost:{" "}
                <b> {nonMemberCost}</b> USD
              </Typography>
            </Trans>
            <Typography variant="body1" sx={{ marginBottom: 32 }}>
              {t(
                "Savings compared to purchasing equivalent classes without \
              membership discounts or marketplace fees."
              )}
            </Typography>
            <Box
              sx={(theme: Theme) => ({
                display: "flex",
                flexDirection: "row",
                gap: 0,
                justifyContent: "space-between",
                [theme.breakpoints.down("md")]: {
                  gap: 16,
                  flexDirection: "column"
                }
              })}
            >
              <TrackedButton
                variant="link"
                trackingName="join_memberships_choose_a_plan_learn_more_modal_view_other_plans_button"
                onClick={() => setIsLearnMoreModalOpen(false)}
              >
                {t("View Other Plans")}
              </TrackedButton>
              <TrackedButton
                variant="contained"
                trackingName="join_memberships_choose_a_plan_learn_more_modal_join_button"
                onClick={onSelectPlan.bind(undefined, suggestedPlan)}
              >
                {t("Join {{suggestedPlanCredits}} credits plan", {
                  suggestedPlanCredits: suggestedPlan.numCredits.toString()
                })}
              </TrackedButton>
            </Box>
          </Box>
        )}
      </Modal>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {isNewBuyer && (
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              alignItems: "center",
              textAlign: "left",
              gap: 36,
              paddingX: 16,
              paddingY: 12,
              backgroundColor: "warning.100",
              borderRadius: 8,
              marginX: 20,
              [theme.breakpoints.down("md")]: {
                gap: 4,
                display: "grid",
                gridTemplateColumns: "auto 1fr"
              },
              [theme.breakpoints.down(375)]: {
                gridTemplateColumns: "auto"
              }
            })}
          >
            <Typography variant="display3">
              {t("$30 OFF!") /** How does i18n factor into this? */}
            </Typography>
            <Typography sx={{ gridColumn: 1 }}>
              {t("Join today and get $30 off your first month*.")}
            </Typography>
            <Box
              flex
              sx={(theme: Theme) => ({
                justifyContent: "flex-end",
                justifySelf: "flex-end",
                [theme.breakpoints.down("md")]: {
                  gridColumn: 2,
                  gridRow: 1
                },
                [theme.breakpoints.down(375)]: {
                  gridColumn: "unset",
                  gridRow: "unset",
                  justifySelf: "center"
                }
              })}
            >
              <Chip
                size={"medium"}
                label={t("CODE: WELCOME30")}
                sx={{ backgroundColor: "white" }}
              />
            </Box>
          </Box>
        )}
        <Box
          ref={containerRef}
          sx={(theme: Theme) => ({
            paddingX: 32,
            paddingY: 24,

            [theme.breakpoints.up("md")]: {
              paddingTop: 55,
              paddingBottom: 45
            },
            display: "flex",
            flexDirection: "row",
            gap: 16,
            overflowX: "auto",
            alignItems: "center",
            justifyContent: isOverflowing ? "flex-start" : "center",
            width: "100%"
          })}
        >
          {SUBSCRIPTION_PLANS.map((plan, index) => (
            <Box
              key={index}
              sx={{
                scrollSnapAlign: "center" // Ensures snapping to center when scrolling,
              }}
            >
              <MembershipOptionCard
                isActive={plan.title === selectedPlan.title}
                isRecommended={plan.title === suggestedPlan.title}
                plan={plan}
                onSelect={onSelectPlan.bind(undefined, plan)}
                sx={{ maxWidth: 256, minHeight: 414 }}
              />
            </Box>
          ))}
        </Box>
        {showSavingsForm && (
          <Box
            flex
            sx={{
              backgroundColor: "white",
              borderRadius: 16,
              paddingX: 24,
              paddingY: 12,
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              width: "100%",
              maxWidth: 1070
            }}
          >
            <Typography variant="body1">
              <Trans t={t}>
                Based on your monthly spend of $&nbsp;
                <Input
                  name="approxMonthlySpendingAmountInput"
                  type="number"
                  min={0}
                  sx={{
                    fontWeight: 500,
                    maxWidth: "80px"
                  }}
                  value={approxMonthlySpendingAmountInput}
                  onKeyUp={e => {
                    if (e.key === "Enter") {
                      setApproxMonthlySpendingAmount(
                        approxMonthlySpendingAmountInput
                      );
                    }
                  }}
                  onChange={e =>
                    setApproxMonthlySpendingAmountInput(
                      parseInt(e.target.value)
                    )
                  }
                  onBlur={e =>
                    setApproxMonthlySpendingAmount(parseInt(e.target.value))
                  }
                />
                &nbsp;USD, we recommend the{" "}
                <b>{{ numCreditsSuggested }} credits plan.</b>
              </Trans>
            </Typography>
            <Box flex sx={{ alignItems: "center" }}>
              <Typography
                sx={{
                  paddingX: 12,
                  paddingY: 4,
                  backgroundColor: "warning.100",
                  fontWeight: "bold",
                  borderRadius: 4
                }}
              >
                {t("Save up to {{currencyString}}!", {
                  currencyString: renderCurrencyString({
                    priceInCents: savingsAmountSuggested,
                    showCurrencyCode: false
                  })
                })}
              </Typography>

              <TrackedButton
                trackingName="join_memberships_choose_a_plan_learn_more_button"
                variant="link"
                size="small"
                sx={{
                  fontSize: 16,
                  marginLeft: "0.5em"
                }}
                onClick={() => setIsLearnMoreModalOpen(true)}
              >
                {t("Learn more")}
              </TrackedButton>
            </Box>
          </Box>
        )}
        {isNewBuyer && (
          <Box
            sx={(theme: Theme) => ({
              textAlign: "center",
              paddingX: 48,
              [theme.breakpoints.down("md")]: {
                paddingX: 24
              }
            })}
          >
            <Typography variant="body2" sx={{ color: "neutral.600" }}>
              {t`*Offer valid for first-time buyers only. Subject to`}{" "}
              <ExternalLink
                sx={{ display: "inline" }}
                url={"http://outschool.com/pages/membership-terms"}
              >
                {t`Outschool Membership Terms.`}
              </ExternalLink>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export const MembershipOptionCard = ({
  plan,
  isRecommended,
  onSelect,
  isActive = false,
  sx
}: {
  plan: {
    title: string;
    numCredits: number;
    centsPerMonth: number;
    savingsPercentage: number;
    classPerWeekAvg: number;
    numRollOverCredits: number;
    numFreeCredits: number;
  };
  isRecommended?: boolean;
  onSelect: (e: React.MouseEvent<HTMLElement>) => void;
  isActive?: boolean;
  sx?: SxProps;
}) => {
  const { t } = useTranslation("client\\routes\\JoinMembershipPage");
  const { isEsaSession } = useSession();
  const [isSavingToolTipOpen, setIsSavingToolTipOpen] = React.useState(false);
  const tooltipRef = React.useRef(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      tooltipRef.current &&
      // @ts-ignore
      !tooltipRef.current.contains(event.target as Node)
    ) {
      setIsSavingToolTipOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: "flex",
        flexDirection: "column",
        paddingY: 20,
        paddingX: 16,
        backgroundColor: "white",
        minWidth: 256,
        minHeight: 412,
        width: "100%",
        borderRadius: 16,
        boxShadow: isActive
          ? "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 32px 0px rgba(0, 0, 0, 0.10)"
          : "unset",
        border: isActive
          ? "1px solid var(--border-border-peacock-darker, #4B01D4)"
          : "1px solid var(--border-border-neutral-lighter, #E0E0E0)",
        "&:hover": {
          cursor: "pointer",
          boxShadow:
            "0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 16px 32px 0px rgba(0, 0, 0, 0.10)"
        },
        ...sx
      }}
    >
      <Box sx={{ marginBottom: 9.5 }}>
        <Typography as="span" variant="subtitle1">
          <b>{plan.numCredits}</b> {t`credits`}
        </Typography>
        {isRecommended && (
          <Chip
            sx={{ display: "inline", marginLeft: 4 }}
            size="small"
            color="primary"
            label={t`Popular`}
          />
        )}
      </Box>
      <Box>
        <Typography as="span" variant="display2" sx={{ marginBottom: 24 }}>
          {renderCurrencyString({
            priceInCents: plan.centsPerMonth,
            showCurrencyCode: false
          })}
        </Typography>{" "}
        <Typography as="span" variant="subtitle1">
          {t`USD`}
        </Typography>
      </Box>
      <Tooltip
        title={
          !!isEsaSession ? (
            <Trans t={t}>
              Memberships aren't available with state ESA, microgrant, or
              scholarship funds. For additional information, contact
              <a href="mailto:support@outschool.com">support@outschool.com</a>.
            </Trans>
          ) : null
        }
      >
        <TrackedButton
          trackingName="join_memberships_choose_a_plan_select_button"
          trackingSharedProperties={{ plan: plan.title }}
          variant={isActive ? "contained" : "outlined"}
          sx={{ marginTop: 24, width: "fit-content" }}
          onClick={onSelect}
          disabled={isEsaSession || Env.IS_READ_ONLY_MODE}
        >
          {t("Select")}
        </TrackedButton>
      </Tooltip>
      <Divider sx={{ marginY: 24 }} />
      <PlanBenefitsList
        plan={plan}
        isSavingToolTipOpen={isSavingToolTipOpen}
        setIsSavingToolTipOpen={setIsSavingToolTipOpen}
        tooltipRef={tooltipRef}
      />
    </Box>
  );
};

function PlanBenefitListItem({ children }: React.PropsWithChildren<{}>) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        marginBottom: 4,
        alignItems: "flex-start"
      }}
    >
      <Box
        sx={{
          marginTop: "1px",
          paddingRight: 4,
          color: "success.main",
          fontSize: 14,
          width: 16
        }}
      >
        •
      </Box>
      <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
        {children}
      </Box>
    </Box>
  );
}

export const PlanBenefitsList = ({
  plan
}: {
  plan: {
    title: string;
    numCredits: number;
    centsPerMonth: number;
    savingsPercentage: number;
    classPerWeekAvg: number;
    numRollOverCredits: number;
    numFreeCredits: number;
  };
  isSavingToolTipOpen?: boolean;
  setIsSavingToolTipOpen?: (x: boolean) => void;
  tooltipRef?: React.MutableRefObject<null>;
}) => {
  const numRollOverCredits = plan.numRollOverCredits;
  const numFreeCredits = plan.numFreeCredits;
  const creditValue = renderCurrencyString({
    priceInCents: OSSubscriptions.creditsToPriceCents(numFreeCredits),
    showCurrencyCode: false
  });
  const classPerWeekAvg = plan.classPerWeekAvg;
  const { t } = useTranslation("client\\routes\\JoinMembershipPage");

  return (
    <>
      <PlanBenefitListItem>
        <Typography variant={"body2"}>
          <Trans t={t}>
            <b>{{ numFreeCredits }} free credits</b>/mo ({{ creditValue }}{" "}
            value)
          </Trans>
        </Typography>
      </PlanBenefitListItem>
      <PlanBenefitListItem>
        <Typography variant={"body2"}>
          {plan.classPerWeekAvg === 1 ? (
            <Trans t={t}>
              Good for an average of <b>{{ classPerWeekAvg }}</b> group class
              per week; add credits anytime
            </Trans>
          ) : (
            <Trans t={t}>
              Good for an average of <b>{{ classPerWeekAvg }}</b> group classes
              per week; add credits anytime
            </Trans>
          )}
        </Typography>
      </PlanBenefitListItem>
      <PlanBenefitListItem>
        <Typography variant={"body2"}>
          <Trans t={t}>
            Roll over <b>up to {{ numRollOverCredits }}</b> credits
          </Trans>
        </Typography>
      </PlanBenefitListItem>
      {(plan.title === "Gold" || plan.title === "Premium") && (
        <PlanBenefitListItem>
          <Typography variant={"body2"}>{t("Concierge support")}</Typography>
          <Tooltip
            sx={{
              width: "fit-content"
            }}
            title={
              <Typography>
                {t(
                  "Get exclusive access to our Concierge — a pilot program designed to \
                enhance your Membership experience. Enjoy priority support, personalized \
                class recommendations, and enrollment guidance."
                )}
              </Typography>
            }
            placement="top"
            describeChild
          >
            <Icon
              size="inherit"
              icon={faQuestionCircle}
              sx={{
                marginLeft: 4,
                fontSize: 14
              }}
            />
          </Tooltip>
        </PlanBenefitListItem>
      )}
    </>
  );
};

export const WhatHappensToMyEnrollmentModal = ({
  isOpen,
  onClose,
  enrollments
}: {
  isOpen: boolean;
  onClose: () => void;
  enrollments: NonNullable<
    NonNullable<
      ChooseAPlanEnrollmentsQueryQuery["currentUser"]
    >["activeEnrollments"]
  >;
}) => {
  const { t } = useTranslation("client\\routes\\JoinMembershipPage");
  const isMobile = useIsMobile();
  const trackEvent = useTrackEvent();
  const { currentUser } = useSession();
  const theme = useTheme();

  const { start: startOfIsoNextWeek, end: endOfIsoNextWeek } =
    React.useMemo(() => {
      const startOfWeek = Time.endOfIsoWeekUtc();
      return {
        start: startOfWeek,
        end: dayjs(startOfWeek).add(1, "week").toDate()
      };
    }, []);

  const recurringEnrollments = (enrollments || [])
    .filter(enrollment => {
      // if 1:1 enrollment only consider it if there's meetings next week
      if (enrollment?.section?.activity?.hasTeacherSchedule) {
        return enrollment.section.meetings.some(
          meeting =>
            dayjs(meeting.start_time).isBetween(
              startOfIsoNextWeek,
              endOfIsoNextWeek,
              null,
              "[]"
            ) && !meeting.paidAt
        );
      } else if (enrollment?.ongoingStartTime || enrollment?.paymentSchedule) {
        return true;
      }
      return false;
    })
    .map(enrollment => {
      const { section, paymentSchedule, price_cents, service_fee_percentage } =
        enrollment || {};
      const { activity, meetings } = section || {};

      const credits = OSSubscriptions.priceCentsToCredits(
        paymentSchedule?.amountCents || price_cents!
      );

      if (activity?.hasTeacherSchedule) {
        const numMeetings =
          meetings?.filter(
            meeting =>
              dayjs(meeting.start_time).isBetween(
                startOfIsoNextWeek,
                endOfIsoNextWeek,
                null,
                "[]"
              ) && !meeting.paidAt
          ).length || 0;

        return {
          uid: activity?.uid,
          classTitle: activity?.title,
          scheduleType: t`Weekly`,
          amountCents: applyServiceFee(
            price_cents! * numMeetings,
            service_fee_percentage ?? 0
          ),
          credits: credits * numMeetings,
          projectedMonthlyCredits:
            paymentSchedule?.scheduleType === PaymentScheduleTypes.MonthlyFirst
              ? credits
              : credits * 4,
          hasMarketplaceFee:
            !!service_fee_percentage && service_fee_percentage > 0
        };
      }

      return {
        uid: activity?.uid,
        classTitle: activity?.title,
        scheduleType:
          // Since we've pre-filtered enrollments, we can assume weekly in all other cases.
          paymentSchedule?.scheduleType === PaymentScheduleTypes.MonthlyFirst
            ? t`Monthly`
            : t`Weekly`,
        amountCents: applyServiceFee(
          paymentSchedule?.amountCents || price_cents!,
          service_fee_percentage ?? 0
        ),
        credits: credits,
        projectedMonthlyCredits:
          paymentSchedule?.scheduleType === PaymentScheduleTypes.MonthlyFirst
            ? credits
            : credits * 4,
        hasMarketplaceFee:
          !!service_fee_percentage && service_fee_percentage > 0
      };
    });

  const displayMarkplaceFeeLegend = recurringEnrollments.some(
    enrollment => enrollment.hasMarketplaceFee
  );

  React.useEffect(() => {
    trackEvent("join_memberships_what_happens_to_my_enrollment_modal_view", {
      userUid: currentUser?.uid
    });
  }, [trackEvent, currentUser]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "70vh",
          flexGrow: 1,
          marginTop: 4,
          paddingRight: 24,
          marginRight: -24,
          overflowY: "auto"
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold" }}
        >{t`What happens to my enrollment?`}</Typography>
        <Box sx={{ marginY: 24 }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Icon
              icon={faPartyHorn}
              sx={{ color: "success.main", fontSize: 20 }}
            />
            <Box>
              <Typography variant="h6">{t`No Action Required`}</Typography>
              <Typography
                variant="body1"
                sx={{ color: "grey.500" }}
              >{t`All classes remain on schedule.`}</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ marginBottom: 8 }}>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Icon
              icon={faInfoCircle}
              sx={{ color: "success.main", fontSize: 20 }}
            />
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  alignItems: "center"
                }}
              >
                <Box sx={{ flex: 3 }}>
                  <Typography variant="h6">{t`Review upcoming charges`}</Typography>
                  <Typography
                    variant="body1"
                    sx={(theme: Theme) => ({ color: theme.palette.grey[600] })}
                  >{t`The following classes will be charged in credits. Any marketplace fees have been removed, and prices are adjusted at a rate of 1 credit = $0.50.`}</Typography>
                </Box>
                <Box
                  sx={(theme: Theme) => ({
                    flex: 1,
                    padding: "0.5em 1em",
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: "0.5em",
                    justifyContent: "center",
                    alignItems: "center"
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    <CoinIcon
                      fillColor={theme.palette.warning.main}
                      strokeColor={theme.palette.warning[700]}
                    />{" "}
                    <Typography variant="h5" emphasized sx={{ marginLeft: 4 }}>
                      {recurringEnrollments.reduce(
                        (total, enrollment) =>
                          total + (enrollment.projectedMonthlyCredits || 0),
                        0
                      )}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={(theme: Theme) => ({
                      color: theme.palette.grey[600],
                      display: "block",
                      textAlign: "center"
                    })}
                  >{t`Projected Credits for Month 1`}</Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  marginTop: "1em"
                }}
              >
                {recurringEnrollments.map((enrollment, index) => (
                  <Box key={enrollment.uid}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        paddingY: 8
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          maxWidth: isMobile ? "100%" : 240,
                          display: "-webkit-box",
                          textOverflow: "ellipsis",
                          WebkitLineClamp: "2",
                          overflow: "hidden",
                          WebkitBoxOrient: "vertical"
                        }}
                      >
                        {enrollment.classTitle}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ marginX: 8 }}>
                          {renderCurrencyString({
                            priceInCents: enrollment.amountCents,
                            showCurrencyCode: false
                          })}
                          {enrollment.hasMarketplaceFee && "*"}
                        </Typography>
                        <Icon icon={faArrowRight} sx={{ fontSize: 14 }} />
                        <Typography variant="subtitle2" sx={{ marginX: 8 }}>
                          {enrollment.credits} {t`credits`}
                        </Typography>
                        <Chip
                          size="small"
                          label={enrollment.scheduleType}
                          sx={(theme: Theme) => ({
                            minWidth: 70,
                            color: theme.palette.grey[600]
                          })}
                        />
                      </Box>
                    </Box>
                    {index !== recurringEnrollments.length - 1 && <Divider />}
                  </Box>
                ))}
                {displayMarkplaceFeeLegend && (
                  <>
                    <Divider />
                    <Typography
                      variant="caption"
                      sx={(theme: Theme) => ({
                        marginTop: "1em",
                        color: theme.palette.grey[600]
                      })}
                    >
                      {t("* Classes with marketplace fees.")}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            marginTop: 32
          }}
        >
          <TrackedButton
            variant="link"
            trackingName="join_memberships_what_happens_to_my_enrollment_modal_back_to_plans_button"
            onClick={onClose}
            sx={{
              // Odd fix to prevent overflow from happening automatically.
              lineHeight: "1.25"
            }}
          >
            {t`Back to Plans`}
          </TrackedButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ChooseAPlan;
